<script setup>
import { useRoute, useRouter } from 'vue-router'
import { onMounted, provide, ref, useTemplateRef } from 'vue'
import { useDropdown } from '@/composables/useDropdown.js'
import { getCompiledPoll } from '@/services/compiledPollService.js'
import { splitSuppliers } from '@/libs/utils.js'

import PollComponent from '@/components/DefaultVr/Components/PollComponent.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'
import ButtonSalva from '@/components/Generics/Buttons/ButtonSalva.vue'

import { toast } from '@/services/toast.js'
import { client } from '@/services/fetch.js'
import { formatTransition, transitionsConfig } from '@/libs/utils/workflows.js'
import TranslationProvider from '@/provider/translations.js'

const route = useRoute()
const router = useRouter()
const props = defineProps(['updatePageTitle'])
const { dropDownToggleF, hideDropdowns } = useDropdown()
const compiledPollId = route['params']['id']

let compiledPoll = ref(null)
let isLoading = ref(true)
let form = ref(null)
let answers = ref([])
let networkError = ref({
  enabled: false
})

const formErrors = ref([])
const questionsErrors = ref([])

const modalConfirmChangeState = useTemplateRef('modal-confirm-change-state')
const currCompiledPoll = ref(null)
const currTransitionToApply = ref(null)

const loadingSave = ref(false)

const modalReasonChangeState = useTemplateRef('modal-reason-change-state')
const reasonChangeState = defineModel('reasonChangeState')
const reasonChangeStateNote = defineModel('reasonChangeStateNote')
const transitionConfigs = ref(transitionsConfig)
const reasonChangeStateValidationError = ref(false)
const reasonChangeStateNoteValidationError = ref(false)
const currTransitionConfig = ref(null)

const handleAnswer = (questionReference, value, remove, note = null) => {
  if (!remove) {
    let exists = false
    answers.value.forEach(el => {
      if (el['questionReference'] === questionReference) {
        el.value = value
        el.note = note
        exists = true
      }
    })
    if (!exists) {
      answers.value.push({
        questionReference: questionReference,
        value: value,
        note: note
      })
    }
  } else {
    answers.value.forEach((el, index) => {
      if (el['questionReference'] === questionReference) {
        answers.value.splice(index, 1)
      }
    })
  }
}

const compilePoll = async (
  event,
  transition = null,
  confirm = false,
  checkTransition = false
) => {
  if (!confirm) {
    currCompiledPoll.value = compiledPoll.value
    currTransitionToApply.value = transition
    modalConfirmChangeState.value.classList.add('d-block')
  } else {
    formErrors.value = []
    questionsErrors.value = []
    loadingSave.value = true
    networkError.value.enabled = false

    if (reasonChangeState.value) {
      currCompiledPoll.value['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason'] = reasonChangeState.value
      currCompiledPoll.value['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason.note'] = reasonChangeStateNote.value
    }

    let data = {
      userReference: currCompiledPoll.value['userReference'],
      campaign: currCompiledPoll.value['campaign'],
      answers: answers.value,
      pollTemplateRevisionNumber: currCompiledPoll.value['pollTemplateRevisionNumber'],
      attributes: currCompiledPoll.value['attributes']
    }

    try {
      if (transition) {
        if (
          checkTransition &&
          transitionConfigs.value.some(tc =>
            currTransitionToApply.value['key'].endsWith(tc.key)
          )
        ) {
          transitionConfigs.value.forEach(tc => {
            if (
              currTransitionToApply.value['key'].endsWith(tc.key)
            ) {
              currTransitionConfig.value = tc
            }
          })

          modalConfirmChangeState.value.classList.remove('d-block')
          modalReasonChangeState.value.classList.add('d-block')
        } else {
          let url = `/compiled-poll/api/compiled-poll/${currCompiledPoll.value['id']}?poll=1`
          await client.put(url, data)

          url = `/compiled-poll/api/compiled-poll/${currCompiledPoll.value['id']}/workflow-transitions/${currTransitionToApply.value['key']}`
          await client.post(url)
          compiledPoll.value = await getCompiledPoll(compiledPollId)

          reasonChangeStateNote.value = null
          reasonChangeState.value = null

          currCompiledPoll.value = null
          currTransitionToApply.value = null
          modalConfirmChangeState.value.classList.remove('d-block')

          toast.create(`Somministrazione <b>${compiledPoll.value.id}</b> aggiornata con successo`, 'success')
        }
      } else {
        let url = `/compiled-poll/api/compiled-poll/${currCompiledPoll.value['id']}?poll=1`
        await client.put(url, data)
        currCompiledPoll.value = null
        currTransitionToApply.value = null
        modalConfirmChangeState.value.classList.remove('d-block')
        toast.create(`Somministrazione <b>${compiledPoll.value.id}</b> aggiornata con successo`, 'success')
      }
    } catch (error) {
      if (error.data && error.data.errors) {
        for (let e of error.data.errors) {
          if (e.params !== undefined) {
            if (e.params['dpa.error.type'] === 'form') {
              formErrors.value.push(e)
            } else {
              questionsErrors.value.push(e)
            }
          } else {
            console.error(e)
          }
        }
      }
      if (formErrors.value.length > 0) {
        networkError.value.enabled = true
        toast.create('Errore nel salvataggio della compilazione, riprovare', 'error')
      }else{
        toast.create('Errore nel salvataggio della compilazione, controllare le risposte e riprovare', 'error')
      }
      window.scrollTo(0,0);
    }

    modalConfirmChangeState.value.classList.remove('d-block')
    loadingSave.value = false
  }
}

const handleReasonModal = async () => {
  reasonChangeStateValidationError.value = false
  reasonChangeStateNoteValidationError.value = false
  if ((currTransitionConfig.value.reasons.length || currTransitionConfig.value.note_enabled) && (!reasonChangeState.value || !reasonChangeStateNote.value)) {
    if (currTransitionConfig.value.reasons.length && !reasonChangeState.value) {
      reasonChangeStateValidationError.value = true
    }
    if (currTransitionConfig.value.note_enabled && !reasonChangeStateNote.value) {
      reasonChangeStateNoteValidationError.value = true
    }
    return
  }
  const cPoll = currCompiledPoll.value['id']
  const url = `/compiled-poll/api/compiled-poll/${cPoll}/add-or-update-attributes`
  try {
    let payload = {}
    payload['attributes'] = {}
    if (currTransitionConfig.value.reasons.length) {
      payload['attributes'][
        `polls.${currTransitionToApply.value['key']}.not_evaluated.reason`
        ] = reasonChangeState.value ?? ''
    }
    if (currTransitionConfig.value.note_enabled) {
      payload['attributes'][
        `polls.${currTransitionToApply.value['key']}.not_evaluated.reason.note`
        ] = reasonChangeStateNote.value ?? ''
    }
    if (currTransitionConfig.value.additional_attributes) {
      for (let attr of currTransitionConfig.value.additional_attributes) {
        payload['attributes'][
          `polls.${currTransitionToApply.value['key']}.${attr.key}`
          ] = attr.value.toString()
      }
    }

    if (currTransitionConfig.value.additional_inputs) {
      for (const aInput of currTransitionConfig.value.additional_inputs) {
        if (aInput.value) {
          payload['attributes'][
            `polls.${currTransitionToApply.value['key']}.${aInput.attribute_key}`
            ] = aInput.value.toString()
        }
      }
    }

    const res = await client.put(url, payload)
    if (res && res.data && res.data.id) {
      await compilePoll(
        currCompiledPoll.value,
        currTransitionToApply.value,
        true,
        false
      )
    } else {
      toast.create('Errore nell\'applicazione della transizione, riprovare', 'error')
    }
  } catch (error) {
    toast.create('Errore nell\'applicazione della transizione, riprovare', 'error')
  }

  currCompiledPoll.value = null
  currTransitionToApply.value = null
  reasonChangeStateNote.value = null
  reasonChangeState.value = null
  modalReasonChangeState.value.classList.remove('d-block')
}

provide('questionsErrors', questionsErrors)

onMounted(async () => {
  try {
    compiledPoll.value = await getCompiledPoll(compiledPollId)
  } catch (error) {
    if(error.data.status === 403) {
      router.replace({ path: '/admin/error_403' })
    }

    if(error.data.status === 404) {
      router.replace({ path: '/admin/error_404' })
    }
  }

  let pageTitle = splitSuppliers(compiledPoll.value['attributes']['gec.suppliers']) + ' - ' + compiledPoll.value['poll']['title']
  props.updatePageTitle(pageTitle)
  isLoading.value = false
})
</script>

<template>
  <div class="container-fluid" v-if="compiledPoll" @click="hideDropdowns">
    <div class="row">
      <div class="col-12 px-0">
        <div class="card">
          <div class="card-body scrollable-body px-2">
            <div
              v-if="networkError.enabled"
              class="alert alert-danger mt-2 mx-2"
              role="alert"
            >
              <div
                v-if="formErrors.length"
              >
                <div v-for="(e, idx) in formErrors" :key="idx" :class="{
                  'pb-1': idx < formErrors.length - 1
                }">{{ e.params['dpa.error.customMessage'] }}</div>
              </div>
            </div>

            <loading v-if="isLoading"></loading>

            <form @submit.prevent="() => {}" ref="form" class="m-2">
              <poll-component
                v-if="compiledPoll"
                :compiled-poll="compiledPoll"
                :handle-answer="handleAnswer"
                :form="form"
              ></poll-component>
            </form>
          </div>

        </div>

        <div class="d-flex mt-2 justify-content-between">
          <router-link
            :to="{ name: 'admin/DefaultVr_CompiledPoll', query: { page: route.query.page, filters: route.query.filters } }"
          >
            <button-torna-alla-lista></button-torna-alla-lista>
          </router-link>

          <div class="dropdown"
               v-if="compiledPoll['enabledActions'] && compiledPoll['enabledActions']['update'] &&
                  compiledPoll['enabledTransitions'].length">
            <button class="btn btn-primary dropdown-toggle dropdown-toggle-b" type="button" id="dropdownMenuButton"
                    title="Salva"
                    @click="dropDownToggleF"
                    ref="dropdown-toggle"
                    data-toggle="dropdown"
                    aria-expanded="false">
              Salva
            </button>

            <div class="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton">
              <button v-if="compiledPoll['enabledActions'] && compiledPoll['enabledActions']['update']"
                 @click="compilePoll"
                 class="dropdown-item"
              >
                Salva
              </button>

              <button v-for="et in compiledPoll['enabledTransitions']" :key="et"
                 @click="compilePoll($event, et)"
                 class="dropdown-item"
              >
                {{ formatTransition(et['labels']['it']) }}
              </button>
            </div>
          </div>
          <div v-else-if="compiledPoll['enabledActions'] && compiledPoll['enabledActions']['update']">
            <button-salva @click="compilePoll"></button-salva>
          </div>

        </div>

        <div style="margin-top: 100px"></div>

      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-confirm-change-state"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" v-if="currTransitionToApply">
            Salvataggio compilazione e cambio stato
          </h5>
          <h5 class="modal-title" v-else>Salvataggio questionario</h5>
          <button
            v-if="!loadingSave"
            type="button"
            class="btn-close border-0 bg-white"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalConfirmChangeState.classList.remove('d-block')
              }
            "
          ></button>
        </div>
        <div class="modal-body" v-if="currCompiledPoll">
          <p v-if="currTransitionToApply"
            v-html="TranslationProvider.getTrans('app.compiled_poll.save_change_state', null, {'{state}': formatTransition(currTransitionToApply['labels']['it'], true)})"
          >
          </p>
          <p v-else>Il questionario verrà salvato, procedere?</p>
        </div>
        <div class="modal-footer">
          <button
            v-if="!loadingSave"
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalConfirmChangeState.classList.remove('d-block')
              }
            "
          >
            Annulla
          </button>
          <button
            type="button"
            :disabled="loadingSave"
            class="btn btn-primary"
            @click="
              compilePoll(
                $event,
                currTransitionToApply,
                true,
                !!currTransitionToApply,
              )
            "
          >
            Procedi
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-reason-change-state"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Selezionare una motivazione</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalReasonChangeState.classList.remove('d-block')
                reasonChangeStateNote = null
                reasonChangeState = null
              }
            "
          ></button>
        </div>
        <div class="modal-body">
          <p>Inserisci la motivazione per questo cambio di stato</p>
          <div>
            <v-select
              v-if="currTransitionConfig && currTransitionConfig.reasons.length"
              :options="currTransitionConfig.reasons"
              v-model="reasonChangeState"
              placeholder="Seleziona una motivazione"
            >
              <template #no-options>
                <div style="opacity: 0.8">{{TranslationProvider.getTrans('app.no_result_found')}}</div>
              </template>
            </v-select>
            <p class="mb-0 text-danger" v-if="reasonChangeStateValidationError">
              Questo campo è obbligatorio
            </p>
          </div>
          <div class="mt-2" v-if="currTransitionConfig && currTransitionConfig.note_enabled">
            <textarea
              class="form-control"
              v-model="reasonChangeStateNote"
              placeholder="Note..."
            >
            </textarea>
            <p
              class="mb-0 text-danger"
              v-if="reasonChangeStateNoteValidationError"
            >
              Questo campo è obbligatorio
            </p>
          </div>

          <template v-if="currTransitionConfig && currTransitionConfig.additional_inputs">
            <div v-for="(aInput, index) in currTransitionConfig.additional_inputs" :key="aInput.attribute_key">
              <label>{{ aInput.label }}</label>
              <input
                :type="aInput.type"
                @input="(event)=>{currTransitionConfig.additional_inputs[index].value = event.target.value}"
                :class="aInput.class"
              >
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalReasonChangeState.classList.remove('d-block')
                reasonChangeStateNote = null
                reasonChangeState = null
              }
            "
          >
            Chiudi
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="loadingSave"
            @click="handleReasonModal"
          >
            <span v-if="!loadingSave">Invia</span>
            <span v-if="loadingSave">
              <span
                class="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
              <span class="visually-hidden" role="status">Loading...</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

:root,
:host {
  @extend :root;
}

[class*='fa-'] {
  width: 0.9rem;
}
</style>
